// src/components/molecules/Video/CinematicPlayer.js
// Path: src/components/molecules/Video/CinematicPlayer.js

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Slider, Typography, CircularProgress, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useTheme } from "styled-components";

/**
 * A cinematic HTML5 video player with custom MUI controls.
 * - 16:9 container; video uses object-fit: contain (letterboxes portrait/odd aspect safely)
 * - Custom controls (play/pause, +/-10s, timeline, volume, fullscreen)
 * - Anti-download UX hardening (not DRM): controlsList, disable PiP, block context menu
 * - Keyboard: Space (play/pause), Left/Right (seek 10s), M (mute), F (fullscreen)
 */
const formatTime = (t) => {
  if (!isFinite(t)) return "0:00";
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  const mTotal = Math.floor(t / 60);
  const m = (mTotal % 60).toString();
  const h = Math.floor(mTotal / 60);
  return h > 0 ? `${h}:${m.padStart(2, "0")}:${s}` : `${m}:${s}`;
};

const CinematicPlayer = ({ src, title, poster, autoPlay = false, onEnded }) => {
  const theme = useTheme();
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [vol, setVol] = useState(1);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(NaN);
  const [isFs, setIsFs] = useState(false);
  const [hovering, setHovering] = useState(true); // show controls initially

  // Hide controls after idle
  useEffect(() => {
    if (!hovering || !playing) return;
    const t = setTimeout(() => setHovering(false), 2000);
    return () => clearTimeout(t);
  }, [hovering, playing]);

  const onMouseMove = () => setHovering(true);

  // Fullscreen helpers
  const enterFs = async () => {
    try {
      if (containerRef.current?.requestFullscreen) await containerRef.current.requestFullscreen();
      setIsFs(true);
    } catch {}
  };
  const exitFs = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      setIsFs(false);
    } catch {}
  };

  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Wire video events
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onLoaded = () => {
      setDuration(v.duration || NaN);
      setReady(true);
      setWaiting(false);
      if (autoPlay) {
        const p = v.play();
        if (p?.catch) p.catch(() => {}); // ignore autoplay block
      }
    };
    const onTime = () => setTime(v.currentTime || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onWaiting = () => setWaiting(true);
    const onCanPlay = () => setWaiting(false);
    const onEndedLocal = () => {
      setPlaying(false);
      onEnded && onEnded();
    };

    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("ended", onEndedLocal);

    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("ended", onEndedLocal);
    };
  }, [autoPlay, onEnded]);

  // Reset state when src changes
  useEffect(() => {
    setReady(false);
    setWaiting(false);
    setPlaying(false);
    setTime(0);
    setDuration(NaN);
  }, [src]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  }, []);

  const seekBy = useCallback((delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(
      Math.max(v.currentTime + delta, 0),
      isFinite(v.duration) ? v.duration : v.currentTime + delta
    );
  }, []);

  const onSeek = (e, val) => {
    const v = videoRef.current;
    if (!v) return;
    setTime(val);
  };
  const onSeekCommit = (e, val) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = val;
  };

  const onVol = (e, val) => {
    const v = videoRef.current;
    if (!v) return;
    setVol(val);
    v.volume = val;
    if (val > 0 && muted) {
      setMuted(false);
      v.muted = false;
    }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    setMuted(next);
    v.muted = next;
  };

  const toggleFs = () => (isFs ? exitFs() : enterFs());

  // Keyboard shortcuts
  const onKeyDown = (e) => {
    if (!containerRef.current) return;
    const keys = [" ", "ArrowLeft", "ArrowRight", "KeyF", "KeyM"];
    if (!keys.includes(e.code)) return;
    e.preventDefault();
    if (e.code === " ") togglePlay();
    if (e.code === "ArrowLeft") seekBy(-10);
    if (e.code === "ArrowRight") seekBy(10);
    if (e.code === "KeyF") toggleFs();
    if (e.code === "KeyM") toggleMute();
  };

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      role="region"
      aria-label="Video player"
      onKeyDown={onKeyDown}
      onMouseMove={onMouseMove}
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "20px",
        overflow: "hidden",
        border: `1px solid ${theme.colors.palePurple}`,
        backgroundColor: "black",
        // Maintain 16:9; video letterboxes inside via objectFit: contain
        aspectRatio: "16 / 9",
        userSelect: "none",
      }}
    >
      {/* Background overlay for cinematic vibe */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.6) 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Video element */}
      <Box sx={{ position: "absolute", inset: 0 }}>
        <video
          ref={videoRef}
          src={src || ""}
          poster={poster || ""}
          playsInline
          preload="metadata"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain", // keeps portrait intact with pillarboxing
            backgroundColor: "black",
          }}
          controls={false}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
        />
      </Box>

      {/* Big center play when paused */}
      {!playing && ready && !waiting && (
        <IconButton
          aria-label="Play"
          onClick={togglePlay}
          sx={{
            position: "absolute",
            zIndex: 2,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 88,
            height: 88,
            bgcolor: "rgba(0,0,0,0.5)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <PlayArrowIcon sx={{ color: "white", fontSize: 58 }} />
        </IconButton>
      )}

      {/* Loading spinner */}
      {waiting && (
        <Box
          sx={{
            position: "absolute",
            zIndex: 2,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress sx={{ color: theme.colors.amber }} />
        </Box>
      )}

      {/* Controls */}
      {(hovering || !playing) && (
        <Box
          sx={{
            position: "absolute",
            zIndex: 3,
            left: 0,
            right: 0,
            bottom: 0,
            p: { xs: 1.25, sm: 1.5 },
            color: "white",
            fontFamily: "basic-sans",
          }}
        >
          {/* Title + time row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
              px: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ flex: 1, opacity: 0.9 }}>
              {title || ""}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {formatTime(time)} / {isFinite(duration) ? formatTime(duration) : "0:00"}
            </Typography>
          </Box>

          {/* Timeline */}
          <Slider
            aria-label="Seek"
            value={Math.min(time, isFinite(duration) ? duration : time)}
            min={0}
            max={isFinite(duration) ? duration : 0}
            step={0.1}
            onChange={onSeek}
            onChangeCommitted={onSeekCommit}
            sx={{
              height: 6,
              "& .MuiSlider-track": { border: "none" },
              "& .MuiSlider-rail": { opacity: 0.3, backgroundColor: "white" },
              "& .MuiSlider-thumb": {
                width: 10,
                height: 10,
                bgcolor: "white",
              },
              mb: 0.5,
            }}
          />

          {/* Transport */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Rewind 10 seconds">
                <IconButton aria-label="Rewind 10 seconds" onClick={() => seekBy(-10)} size="small">
                  <Replay10Icon sx={{ color: "white" }} />
                </IconButton>
              </Tooltip>

              {playing ? (
                <Tooltip title="Pause">
                  <IconButton aria-label="Pause" onClick={togglePlay} size="small">
                    <PauseIcon sx={{ color: "white" }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Play">
                  <IconButton aria-label="Play" onClick={togglePlay} size="small">
                    <PlayArrowIcon sx={{ color: "white" }} />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Forward 10 seconds">
                <IconButton aria-label="Forward 10 seconds" onClick={() => seekBy(10)} size="small">
                  <Forward10Icon sx={{ color: "white" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title={muted ? "Unmute" : "Mute"}>
                <IconButton aria-label={muted ? "Unmute" : "Mute"} onClick={toggleMute} size="small">
                  {muted || vol === 0 ? (
                    <VolumeOffIcon sx={{ color: "white" }} />
                  ) : (
                    <VolumeUpIcon sx={{ color: "white" }} />
                  )}
                </IconButton>
              </Tooltip>

              <Slider
                aria-label="Volume"
                value={vol}
                min={0}
                max={1}
                step={0.05}
                onChange={onVol}
                sx={{
                  width: 100,
                  "& .MuiSlider-track": { border: "none" },
                  "& .MuiSlider-rail": { opacity: 0.3, backgroundColor: "white" },
                  "& .MuiSlider-thumb": {
                    width: 10,
                    height: 10,
                    bgcolor: "white",
                  },
                }}
              />
            </Box>

            <Tooltip title={isFs ? "Exit full screen" : "Full screen"}>
              <IconButton aria-label={isFs ? "Exit full screen" : "Full screen"} onClick={toggleFs} size="small">
                {isFs ? (
                  <FullscreenExitIcon sx={{ color: "white" }} />
                ) : (
                  <FullscreenIcon sx={{ color: "white" }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CinematicPlayer;
